import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OneShotTask, Task } from '../../interface/task.interface';

import { TaskAssignee } from '../../interface/task_assignee.interface';
import { BridgeService } from '../bridge/bridge.service';

@Injectable({
    providedIn: 'root',
})
export class TasksService {
    constructor(private _bridge: BridgeService) {}

    /**
     * Method to get task
     * @param data Contains task id and task type
     */
    getTask = (data: { taskId: string; taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE' }) => {
        return new Observable(observer => {
            this._bridge.getTask(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to create task
     * @param data Contains task object and task type
     */
    createTask = (data: { task: Task; taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE' }) => {
        return new Observable(observer => {
            this._bridge.createTask(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to create a one-shot (non-recurring) task instance under an existing task instance
     * @param task One-shot task payload
     */
    createOneShotTask = (task: OneShotTask) => {
        return new Observable(observer => {
            this._bridge.createOneShotTask(
                task,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to delete or disable task
     * @param data Contains task id and task type
     */
    deleteOrDisableTask = (data: { task: Task; enabled: boolean }) => {
        return new Observable(observer => {
            this._bridge.deleteOrDisableTask(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to update task
     * @param task Task object
     */
    updateTaskFromList = (task: Task) => {
        return new Observable(observer => {
            this._bridge.updateTaskFromList(
                task,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to get task assignees
     * @param data Contains task id and task type
     */
    getTaskAssignees = (data: { taskId: string; taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE' }) => {
        return new Observable(observer => {
            this._bridge.getTaskAssignees(
                data,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to create task link
     * @param taskId Task id
     * @param linkData Data for creating link { app_name, event_type, expression, task_definition_id, custom_message }
     */
    createLink = (
        taskId: string,
        linkData: {
            app_name: string;
            event_type: string;
            expression: string;
            task_definition_id: string;
            id?: string;
            custom_message?: string;
            service_user_id: string;
            organization?: string;
        }
    ) => {
        return new Observable(observer => {
            this._bridge.createLink(
                taskId,
                linkData,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /** Method to get task links
     * @param taskId Task id
     */

    getLinks = (taskId: string) => {
        return new Observable(observer => {
            this._bridge.getLinks(
                taskId,
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /** Method to delete task link
     * @param taskId Task id
     * @param linkId Link id
     */

    deleteLink = (taskId: string, linkId: string) => {
        return new Observable(observer => {
            this._bridge.deleteLink(
                taskId,
                linkId,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to create task assignees
     * @param taskId Task id
     * @param assignees Assignee objects
     */
    updateTaskAssignees = (taskId: string, taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE', assignees: TaskAssignee[]) => {
        return new Observable(observer => {
            this._bridge.updateTaskAssignees(
                taskId,
                taskType,
                assignees,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to delete task assignee
     * @param taskId Task id
     */
    deleteTaskAssignee = (taskId: string, principals: string[], taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE') => {
        return new Observable(observer => {
            this._bridge.deleteTaskAssignee(
                taskId,
                principals,
                taskType,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    getTasks = () => {
        return new Observable(observer => {
            this._bridge.getTasks(
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    getChildTasks = (data: { taskId: string; taskType: 'TASK_DEFINITION' | 'TASK_INSTANCE' }) => {
        return new Observable(observer => {
            this._bridge.getChildTasks(
                data,
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to get task instances
     * @param taskId Task id
     */
    getTaskInstances = (taskId: string) => {
        return new Observable(observer => {
            this._bridge.getTaskInstances(
                taskId,
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to get status groups
     */

    getStatusGroups = () => {
        return new Observable(observer => {
            this._bridge.getStatusGroups(
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to create a status group
     * @param payload Status group object
     */

    createStatusGroup = (payload: { id?: string; name: string; description: string; is_default: boolean; statuses: any[] }) => {
        return new Observable(observer => {
            this._bridge.createStatusGroup(
                payload,
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                err => {
                    observer.error(err?.response || 'Failed to create status group');
                    observer.complete();
                }
            );
        });
    };

    getFutureTaskInstances = (taskId: string, start: string, end: string) => {
        return this._bridge.getFutureTaskInstances(taskId, start, end);
    };

    getComments = (data: { taskId: string }) => {
        return this._bridge.getComments(data);
    };

    getActionUpdates = (data: { taskId: string }) => {
        return this._bridge.getActionUpdates(data);
    };

    createComment = (data: { taskId: string; formData: FormData }) => {
        return this._bridge.createComment(data);
    };

    updateComment = (data: { taskId: string; commentId: string; formData: FormData }) => {
        return this._bridge.updateComment(data);
    };

    downloadCommentFile = (data: { taskId: string; fileURL: string }) => {
        this._bridge.downloadCommentFile(data);
    };

    getChildTaskInstancesWithDetails = (taskId: string) => {
        return this._bridge.getChildTaskInstancesWithDetails(taskId);
    };

    searchChildTaskInstancesWithDetails = (rootInstanceId: string, filter: any) => {
        return this._bridge.searchChildTaskInstancesWithDetails(rootInstanceId, filter);
    };

    getChildTaskDefintionsWithDetails = (taskId: string) => {
        return this._bridge.getChildTaskDefinitionsWithDetails(taskId);
    };

    getRootTaskInstances = (taskDefinitionId: string) => {
        return this._bridge.getRootTaskInstances(taskDefinitionId);
    };

    /**
     * Method to create task notifier
     * @param taskId Task id
     * @param notifierData Data for creating notifier { event_type, schedule, roles, users, definition_id }
     */
    createNotifier = (
        taskId: string,
        notifierData: {
            event_type: string;
            schedule: string;
            roles: { id: string; name: string }[];
            users: { id: string; name: string }[];
            definition_id: string;
        }
    ) => {
        return new Observable(observer => {
            this._bridge.createNotifier(
                taskId,
                notifierData,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /**
     * Method to update task notifier
     * @param taskId Task id
     * @param notifierData Data for updating notifier { event_type, schedule, roles, users, definition_id }
     */
    updateNotifier = (
        taskId: string,
        notifierData: {
            event_type: string;
            schedule: string;
            roles: { id: string; name: string }[];
            users: { id: string; name: string }[];
            definition_id: string;
        }
    ) => {
        return new Observable(observer => {
            this._bridge.updateNotifier(
                taskId,
                notifierData,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /** Method to get task notifiers
     * @param taskId Task id
     */

    getNotifiers = (taskId: string) => {
        return new Observable(observer => {
            this._bridge.getNotifiers(
                taskId,
                res => {
                    observer.next(res?.response || res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.msg);
                    observer.complete();
                }
            );
        });
    };

    /** Method to delete task notifier
     * @param taskId Task id
     * @param eventType Instance Event Type
     */

    deleteNotifier = (taskId: string, eventType: string) => {
        return new Observable(observer => {
            this._bridge.deleteNotifier(
                taskId,
                eventType,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };

    /** Method to delete comment
     * @param taskId Task id
     * @param commemntId Link id
     */

    deleteComment = (taskId: string, commentId: string) => {
        return new Observable(observer => {
            this._bridge.deleteComment(
                taskId,
                commentId,
                res => {
                    observer.next(res);
                    observer.complete();
                },
                res => {
                    observer.error(res?.response);
                    observer.complete();
                }
            );
        });
    };
    getTaskActions = (taskDefinitionId: string) => {
        return this._bridge.getTaskActions(taskDefinitionId);
    };

    /** Method to save task action
     * @param taskDefinitionId Task definition id
     * @param actionData Action data
     */
    saveTaskAction = (taskDefinitionId: string, actionData: any) => {
        return this._bridge.saveTaskAction(taskDefinitionId, actionData);
    };

    /** Method to update task action
     * @param taskDefinitionId Task definition id
     * @param actionId Action id
     * @param actionData Action data
     */
    updateTaskAction = (taskDefinitionId: string, actionId: string, actionData: any) => {
        return this._bridge.updateTaskAction(taskDefinitionId, actionId, actionData);
    };

    /** Method to delete task action
     * @param taskDefinitionId Task definition id
     * @param actionId Action id
     */
    deleteTaskAction = (taskDefinitionId: string, actionId: string) => {
        return this._bridge.deleteTaskAction(taskDefinitionId, actionId);
    };
}
